import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import Header from '../components/layout/Header';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { refreshCart } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to check payment status');

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Refresh cart to clear local state
          await refreshCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling if still pending
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, refreshCart]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] via-[#2d1f3d] to-[#1a1a1a]">
      <AnnouncementBar />
      <Header />
      <main className="py-20">
        <div className="max-w-lg mx-auto px-4 text-center">
          {status === 'loading' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-12 shadow-lg">
              <Loader2 className="w-16 h-16 mx-auto text-white animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
              <p className="text-white/60">Please wait while we confirm your payment...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-12 shadow-lg">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
              <p className="text-white/60 mb-6">
                Thank you for your order. You will receive an email confirmation shortly.
              </p>
              {paymentData && (
                <p className="text-sm text-white/50 mb-8">
                  Total paid: {formatPrice(paymentData.amount_total / 100)}
                </p>
              )}
              <div className="space-y-3">
                <Link
                  to="/shop"
                  className="block w-full bg-white text-black py-4 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded"
                >
                  CONTINUE SHOPPING
                </Link>
                <Link
                  to="/"
                  className="block w-full border border-white/30 text-white py-4 text-sm font-medium tracking-wider hover:bg-white/10 transition-colors rounded"
                >
                  BACK TO HOME
                </Link>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-12 shadow-lg">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Error</h1>
              <p className="text-white/60 mb-8">
                There was an issue processing your payment. Please try again.
              </p>
              <Link
                to="/cart"
                className="block w-full bg-white text-black py-4 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded"
              >
                RETURN TO CART
              </Link>
            </div>
          )}

          {status === 'timeout' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-12 shadow-lg">
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⏱️</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Pending</h1>
              <p className="text-white/60 mb-8">
                Your payment is still being processed. Please check your email for confirmation.
              </p>
              <Link
                to="/"
                className="block w-full bg-white text-black py-4 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded"
              >
                BACK TO HOME
              </Link>
            </div>
          )}

          {status === 'expired' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-12 shadow-lg">
              <div className="w-20 h-20 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⏰</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
              <p className="text-white/60 mb-8">
                Your checkout session has expired. Please try again.
              </p>
              <Link
                to="/cart"
                className="block w-full bg-white text-black py-4 text-sm font-medium tracking-wider hover:bg-gray-200 transition-colors rounded"
              >
                RETURN TO CART
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
